import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Logo } from "@/components/brand";
import type { ReactNode } from "react";

import { AppShell, NavGroup, NavItem, SessionStatus } from "@/components/ui";

import "../console.css";

import { ChooseHandle } from "../account/ChooseHandle";
import { useAccount } from "../account/lib/queries";
import { CascadeOutcomes } from "../components/CascadeOutcomes";
import {
  useLoadedSpace,
  useSpaceAgents,
  useSpaceRows,
} from "../spaces/lib/queries";

import { AccountMenu } from "./AccountMenu";
import { LoadedSpaceControl } from "./LoadedSpaceControl";
import { ViewsPicker } from "./ViewsPicker";

const SECTIONS = [
  { id: "home", label: "Home", to: "/console/home" },
  { id: "spaces", label: "Spaces", to: "/console/spaces" },
  { id: "activities", label: "Activities", to: "/console/activities" },
  { id: "studio", label: "Studio", to: "/console/studio" },
] as const;

const ACCOUNT_SECTIONS = [
  { id: "machines", label: "Machines", to: "/console/machines" },
  { id: "feeds", label: "Feeds", to: "/console/feeds" },
  { id: "people", label: "People", to: "/console/people" },
  { id: "plan", label: "Plan", to: "/console/plan" },
  { id: "billing", label: "Billing", to: "/console/billing" },
  { id: "account", label: "Account", to: "/console/account" },
] as const;

/** The rail is the console and holds nothing else. */
export function ConsoleShell({ children }: { readonly children: ReactNode }) {
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const account = useAccount();
  const { query } = useSpaceRows();
  const loaded = useLoadedSpace();
  const machines = useSpaceAgents(loaded.data?.space_id);

  const loadedRow =
    (query.data?.spaces ?? []).find(
      (one) => one.space.space_id === loaded.data?.space_id,
    ) ?? null;
  const running = loadedRow?.live_activity_count ?? 0;

  /**
   * The machines ATTACHED to this space, which is not every machine that serves it: a paired
   * machine follows the loaded space in place and only needs a connection for a local-tier
   * requirement. No rows is silence here, never a claim that no machine is there.
   */
  const attached = machines.data?.agents ?? [];
  const up = attached.filter((one) => one.state === "connected").length;
  const machineDetail =
    attached.length === 0
      ? ""
      : `, ${String(up)}/${String(attached.length)} machines up`;

  const item = (section: { id: string; label: string; to: string }) => (
    <NavItem
      key={section.id}
      href={section.to}
      active={pathname.startsWith(section.to)}
      onClick={(event: { preventDefault: () => void }) => {
        event.preventDefault();
        void navigate({ to: section.to });
      }}
    >
      {section.label}
    </NavItem>
  );

  const nav = [
    <NavGroup key="console">{SECTIONS.map(item)}</NavGroup>,
    <NavGroup key="account" label="Account">
      {ACCOUNT_SECTIONS.map(item)}
    </NavGroup>,
  ];

  return (
    <>
      <AppShell
        railLabel="Console"
        brand={
          <div className="cx-brand">
            <Logo size={22} />
          </div>
        }
        nav={nav}
        railFooter={
          <div className="cx-live-strip">
            <SessionStatus
              state={running > 0 ? "live" : "off"}
              detail={
                loadedRow === null
                  ? "nothing loaded"
                  : `${String(running)} running, ${loadedRow.space.name ?? loadedRow.space.join_code}${machineDetail}`
              }
            />
            <LoadedSpaceControl />
            <AccountMenu />
          </div>
        }
      >
        <CascadeOutcomes />
        {account.data !== undefined && !account.data.handle_chosen ? <ChooseHandle /> : children}
      </AppShell>
      <ViewsPicker />
    </>
  );
}
